<template>
  <DropdownHybrid
    v-model="isOpen"
    :sheet-title="t('component.add_button.title')"
  >
    <template #activator="{ props: activatorProps }">
      <BrandButton
        v-bind="{ ...activatorProps, ...$attrs }"
        variant="primary"
        size="large"
        class="pl-3"
      >
        <PhPlus class="text-h5 mr-3" />
        {{ t('cta.add') }}
      </BrandButton>
    </template>

    <v-list bg-color="neutral25" class="py-3">
      <v-list-item @click="onAddBookmark">
        <template #prepend>
          <PhBookmarkSimple class="text-h5 mr-3" />
        </template>
        <v-list-item-title class="text-neutral900 font-weight-medium">
          {{ t('component.add_button.add_bookmark') }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item @click="onAddNote">
        <template #prepend>
          <PhNote class="text-h5 mr-3" />
        </template>
        <v-list-item-title class="text-neutral900 font-weight-medium">
          {{ t('component.add_button.add_note') }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item v-if="imageUploadsEnabled || videoUploadsEnabled" @click="onAddFile">
        <template #prepend>
          <PhImage class="text-h5 mr-3" />
        </template>
        <v-list-item-title class="text-neutral900 font-weight-medium">
          {{ t(videoUploadsEnabled ? 'component.add_button.add_media' : 'component.add_button.add_image') }}
        </v-list-item-title>
      </v-list-item>
    </v-list>
  </DropdownHybrid>
</template>

<script setup>
import { ref, computed, defineProps, defineOptions } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { PhPlus, PhBookmarkSimple, PhNote, PhImage } from '@phosphor-icons/vue'
import DropdownHybrid from '@/components/global/DropdownHybrid.vue'
import BrandButton from '@/components/global/brand/BrandButton.vue'

defineOptions({
  inheritAttrs: false,
})

const props = defineProps({
  collectionId: {
    type: String,
    default: null,
  },
  referrer: {
    type: String,
    default: 'nav',
  },
  // Extra query params appended to all three add routes (e.g. the item picker
  // passes a BACK_TARGET_QUERY so the add flow returns to it after saving).
  extraQuery: {
    type: Object,
    default: null,
  },
})

const router = useRouter()
const store = useStore()
const { t } = useI18n()

const isOpen = ref(false)
// runtime feature flags (admin-toggled); the API has its own kill switches. Photos and videos share
// the one entry, which names videos while they are on.
const imageUploadsEnabled = computed(() => store.getters['features/ImageUploads'])
const videoUploadsEnabled = computed(() => store.getters['features/VideoUploads'])

const addQuery = computed(() => ({
  referrer: props.referrer,
  ...(props.collectionId && { collectionId: props.collectionId }),
  ...props.extraQuery,
}))

const pushAddRoute = path => {
  isOpen.value = false
  router.push({ path, query: addQuery.value })
}

const onAddBookmark = () => pushAddRoute('/bookmarks/add')

const onAddNote = () => pushAddRoute('/notes/add')

const onAddFile = () => pushAddRoute('/files/add')
</script>
